import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import moment from 'moment';
import { duckify, filterDuck } from 'utils/duckify';
import Pagination from 'react-ultimate-pagination-bootstrap-4';
import withPagination from 'hocs/withPagination';
import withStateBasedPagination from 'hocs/withStateBasedPagination';
import Placeholder from 'components/Placeholder';
import ModalRoot from 'components/ModalRoot';
import ModalComponent from 'components/ModalComponent';
import BillingImportJobForm from 'components/billing/BillingImportJobForm';
import { billingImportJobs, billingItems, modals } from 'ducks';
import FontAwesome from 'utils/FA';
import { formatCurrency, formatDate } from 'utils/i18n';

import './styles/BillingImportJobHistory.scss';
import {Link} from "react-router";

const billingItemsPerPage = 50;

const ViewImportResultModalUnduckified = ({ job, ledgerItems, setPage, page }) => {
  const { data: { dry_run: dryRun, result: { count } = {} } } = job;

  const totalPages = Math.ceil(count / billingItemsPerPage);

  const msg = dryRun ? 'No ledger entries were created (Validation only).'
    : `The following (${count}) ledger entries were created:`;
  return (
    <ModalComponent title='Import Results'>
      <p>
        The import file validated successfully. { msg }
      </p>

      {!dryRun &&
        <React.Fragment>
          <div className={'row'}>
            {totalPages > 1 && (
              <div className='pagination pagination-right'>
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  hideFirstAndLastPageLinks
                  onChange={setPage}
                />
              </div>
            )}
          </div>
          <div>
            <Placeholder.loading ready={ledgerItems.ready} content={() =>
              <table className='table'>
                <thead>
                <tr>
                  <th>Contact</th>
                  <th>Billing Account</th>
                  <th>Date</th>
                  <th>Amount</th>
                </tr>
                </thead>
                <tbody>
                {
                  ledgerItems.map((item) => {
                    const { contact: billingAccount } = item.billing_account;

                    return item.billing_item_amounts.map((amount) => {
                      const { billing_contact: { contact } } = amount;

                      return (
                        <tr key={item.id}>
                          <td><a href={`/contacts/${contact.id}`}>{contact.first_name} {contact.last_name}</a></td>
                          <td><a href={`/contacts/${billingAccount.id}/billing/ledger`}>{billingAccount.name}</a></td>
                          <td>{formatDate(item.billing_date)}</td>
                          <td>
                            <span title={`${item.type}: ${item.description}`}>{formatCurrency(amount.amount)}</span>
                          </td>
                        </tr>
                      );
                    });
                  })
                }
                </tbody>
              </table>
            }/>
          </div>
          <div className={'row'}>
            {totalPages > 1 && (
              <div className='pagination pagination-right'>
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  hideFirstAndLastPageLinks
                  onChange={setPage}
                />
              </div>
            )}
          </div>
        </React.Fragment>
      }
    </ModalComponent>
  );
};

const mapResultModalDucks = ({job, page}) => ({ledgerItems: filterDuck(billingItems, {billing_import_job_id: job.id, page, per_page: billingItemsPerPage})});

const ViewImportResultModal =
  compose(
    withStateBasedPagination,
    duckify(mapResultModalDucks)
  )(ViewImportResultModalUnduckified);

const ViewImportErrorModal = ({ job }) => {
  const { data: { error_message: errorMsg, result: { errors = [] } = {} } } = job;
  const allErrors = errorMsg ? [<code>{ errorMsg }</code>, ...errors] : errors;
  return (
    <ModalComponent title='Import Errors'>
      <p>The import failed with the following errors:</p>
      <ul>{ allErrors.map((e, i) => <li key={i}>{ e }</li>) }</ul>
    </ModalComponent>
  );
};

const MODALS = {
  BillingImportJobForm,
  ViewImportResultModal,
  ViewImportErrorModal
};

const ImportDuration = ({ job }) => {
  if (job.status !== 'completed' && job.status !== 'newly_completed') {
    return <span>&ndash;</span>;
  }
  return <span>{(job.data.meta.import_ms / 1000).toFixed(2)} sec.</span>;
};

const ImportStatus = ({ job }) => {
  const { data: { dry_run: dryRun, result: { success } = {} } } = job;

  switch (job.status) {
    case 'starting':
      return <div>Running</div>;
    case 'failed':
      return <div>Error</div>;
    case 'completed':
    case 'newly_completed':
      if (success) {
        return dryRun ? <div>Validation Succeeded</div> : <div>Import Succeeded</div>;
      }
      return <div>Validation Failed</div>;
    default:
      return <div>Pending</div>;
  }
};

const ViewImportJob = connect()(({ job, dispatch }) => {
  const { data: { result: { success } = {} } } = job;
  const type = success ? 'ViewImportResultModal' : 'ViewImportErrorModal';
  const showModal = () => dispatch(modals.actions.show(type, { job }));

  return job.finished ? <a href='#' onClick={() => showModal()}>View</a> : null;
});

const BillingImportTable = ({ jobs, page, setPage }) => {
  const { total, perPage, ready } = jobs;
  const totalPages = Math.ceil(total/perPage);
  const datetimeFormat = `${global.I18n.date.formats.default_moment} h:mm a`;
  const formatDatetime = (date) => moment(date).format(datetimeFormat);

  return (
    <div>
      <Placeholder.loading ready={ready}>
        {totalPages > 1 && (
          <div className='pagination pagination-right'>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              hideFirstAndLastPageLinks
              onChange={setPage}
            />
          </div>
        )}
        <div className='scrollable'>
          <table className='table billing-import-jobs'>
            <thead>
              <tr>
                <th>User</th>
                <th>Date/Time</th>
                <th>Duration</th>
                <th>Description</th>
                <th>Uploaded File</th>
                <th>Import Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((item) => (
                <tr key={item.id}>
                  <td>{item.user_email}</td>
                  <td>{formatDatetime(item.created_at)}</td>
                  <td><ImportDuration job={item} /></td>
                  <td>{item.data.description}</td>
                  <td><a href={`/api/billing_import_jobs/${item.id}/download`}>{item.data.attachment_filename}</a></td>
                  <td><ImportStatus job={item} /></td>
                  <td><ViewImportJob job={item} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Placeholder.loading>
    </div>
  );
};

const CreateImportJobButton = ({ createNewImport }) => (
  <button type='button' className='btn btn-primary' onClick={() => createNewImport()}>
    <FontAwesome name='plus-circle' /> Import Billing Transactions
  </button>
);

const ReloadJobsButton = ({ reload }) => (
  <button type='button' className='btn btn-link' onClick={() => reload()}>
    <FontAwesome name='refresh' /> Reload
  </button>
);

const BillingImportJobHistory = (props) => (
  <div>
    <div className='billing-import-title'>
      <h2>Billing Imports</h2>
      <p>
        Your Billing Import downloads will be available&nbsp;
        <Link to='/setup/user_jobs'>here</Link>
        .
      </p>
      <CreateImportJobButton {...props} />
      <ReloadJobsButton {...props} />
    </div>
    <BillingImportTable {...props} />
    <ModalRoot modals={MODALS} />
  </div>
);

BillingImportJobHistory.propTypes = {
  jobs: PropTypes.array.isRequired,
  page: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  createNewImport: PropTypes.func.isRequired
};

const mapDucksToProps = (props) => ({
  jobs: filterDuck(billingImportJobs, {
    page: props.page || 1,
    per_page: props.perPage,
    ...queryString.parse(props.location.search)
  })
});

const mapDispatchToProps = (dispatch) => ({
  createNewImport: () => dispatch(modals.actions.show('BillingImportJobForm')),
  reload: () => dispatch(billingImportJobs.actions.markDirty())
});

export default compose(withPagination, duckify(mapDucksToProps, () => {}, mapDispatchToProps))(BillingImportJobHistory);
